<template>
    <div class="mt-8">
        <div>
            <h2 class="font-semibold text-slate-800">Lookalikes</h2>
            <p class="text-sm">
                These are creators with similar attributes (industry, category, gender, country, language), or the closest available match, to
                {{ superview.normalized.display_name }}.
            </p>
        </div>

        <div class="mt-4">
            <DiscoverTable
                ref="lookalikes"
                :paging="paging"
                :creators="creators"
                :is-loading="isLoading"
                :columns="lookalikeColumns"
                :enabled-columns="enabledLookalikeColumns"
                :platform-id="platformId"
                :is-selectable="true"
                :is-sortable="true"
                @filter-change="onFilterChange"
                @paging-change="onPagingChange"
                @export-creators="onExportCreators"
            >
                <template #creator="{ field }">
                    <div class="flex my-1.5">
                        <NamePlate
                            :logo="field.logo"
                            :name="field.name"
                            :display-name="field.display_name"
                            :platform-id="field.platform_id"
                            :platform-user-id="field.id"
                            :is-drawer-enabled="true"
                        />
                    </div>
                </template>

                <template #header-toolbar-right>
                    <div class="ml-auto flex">
                        <button
                            class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-1.5 text-sm font-medium hover:bg-accent"
                            @click="$refs.lookalikes.exportCreators()"
                        >
                            <Download class="mr-2 h-4 w-4" />Export
                        </button>
                    </div>
                </template>

            </DiscoverTable>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import numeral from 'numeral'
import { axios } from '@/utils/axios'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import NamePlate from '@/components/plates/NamePlate.vue'
import { Download } from 'lucide-vue-next'
import _ from 'lodash'

const store = useStore()

const props = defineProps({
    platformId: {
        type: String,
        required: true,
    },
    platformUserId: {
        type: String,
        required: true,
    },
    superview: {
        type: Object,
        required: true,
    },
    analysis: {
        type: Object,
        default: null,
    },
    contentType: {
        type: String,
        default: 'video',
    },
})

function getDefaultSort() {
    return {
        field: props.contentType === 'stream' ? 'avg_viewers' : 'avg_views',
        direction: 'desc',
        source: 'default',
    }
}

function resetSorting() {
    paging.value.sort = [getDefaultSort()]
}

// State
const isLoading = ref(false)
const creators = ref([])
const paging = ref({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    perPage: 15,
    sort: [],
})

const lookalikeRetries = {
    current : 0,
    max : 4,
}

// Computed - Column configuration
const lookalikeColumns = computed(() => {
    const columns = [
        {
            id: 'creator',
            key: 'superview',
            header: {
                label: 'Creator',
                is_sortable: false,
            },
            row: {
                slot: 'creator',
                slotTransformer: (item) => ({
                    // eslint-disable-next-line no-underscore-dangle
                    id: item?.superview?._id,
                    name: item?.superview?.normalized?.name,
                    display_name: item?.superview?.normalized?.display_name,
                    logo: item?.superview?.normalized?.avatar_url,
                    platform_id: item?.superview?.platform_id,
                }),
            },
            is_sticky: true,
            group: 'basic',
            is_exportable : true,
            exportTransformer: (item) => ({
                ID: _.get(item, 'superview._id'),
                Name: _.get(item, 'superview.normalized.name'),
                'Platform': _.get(item, 'superview.platform_id'),
            }),
        },
    ]

    // Add platform-specific metric columns
    if (props.platformId === 'twitch') {
        columns.push({
            id: 'avg_viewers',
            key: 'discovery.avg_viewers',
            header: { label: 'Avg Viewers', is_sortable: true },
            row: { formatter: ({ field }) => numeral(field).format('0,0') },
            group: 'metrics',
            is_exportable : true,
        })
        columns.push({
            id: 'followers',
            key: 'superview.normalized.followers',
            header: { label: 'Followers', is_sortable: true },
            row: { formatter: ({ field }) => numeral(field).format('0,0') },
            group: 'metrics',
            is_exportable : true,
        })
    } else {
        columns.push({
            id: 'avg_views',
            key: 'discovery.avg_views',
            header: { label: 'Avg Views', is_sortable: true },
            row: { formatter: ({ field }) => numeral(field).format('0,0') },
            group: 'metrics',
            is_exportable : true,
        })
        columns.push({
            id: 'followers',
            key: 'superview.normalized.followers',
            header: { label: 'Followers', is_sortable: true },
            row: { formatter: ({ field }) => numeral(field).format('0,0') },
            group: 'metrics',
            is_exportable : true,
        })
    }

    columns.push({
        id: 'language',
        key: 'superview.normalized.language',
        header: { label: 'Language', is_sortable: false },
        row: { formatter: ({ field }) => store.getters.getLanguageFromCode(field) || field || 'N/A' },
        group: 'demographics',
        is_exportable : true,
        exportTransformer: (item) => ({
            Language: _.get(item, 'superview.normalized.language', '') && store.getters.getLanguageFromCode(_.get(item, 'superview.normalized.language', '')) !== 'Unknown' ? store.getters.getLanguageFromCode(_.get(item, 'superview.normalized.language', '')) : '',
        }),
    })

    columns.push({
        id: 'country',
        key: 'superview.normalized.country',
        header: { label: 'Country', is_sortable: false },
        row: { formatter: ({ field }) => store.getters.getCountryFromCode(field) || field || 'N/A' },
        group: 'demographics',
        is_exportable : true,
        exportTransformer: (item) => ({
            Country: _.get(item, 'superview.normalized.country', '') ? store.getters.getCountryFromCode(_.get(item, 'superview.normalized.country', '')) : '',
        }),
    })

    return columns
})

const enabledLookalikeColumns = computed(() => {
    const enabled = ['creator']

    if (props.platformId === 'twitch') {
        enabled.push('avg_viewers', 'followers')
    } else {
        enabled.push('avg_views', 'followers')
    }

    enabled.push('language', 'country')

    return enabled
})

const fetchLookalikes = () => {
    isLoading.value = true

    //Iteration 0
    //Iteration 1 Wider view(er) range
    //Iteration 2 Wider view(er) range, remove country
    //Iteration 3 Wider view(er) range, remove country, remove category
    //Iteration 4 Wider view(er) range, remove country, remove category, remove language

    // Build query filters based on creator attributes
    const query = {}

    if (props.superview?._id) {
        query._id = {
                exclude: {
                    values: [{ id : props.superview?._id}],
                },
            }
    }

    // Add industry/category filters from analysis (nested structure)
    if (props.analysis?.primary_classification?.topic) {
        query.classifications = {
            hierarchy: {
                topic: props.analysis.primary_classification.topic,
                index: 0,
            },
            conjunction: '$and',
            primary_only: true,
        }

        // Add category if available
        if (props.analysis?.primary_classification?.category && lookalikeRetries.current < 2) {
            query.classifications.hierarchy.category = props.analysis.primary_classification.category
        }
    }

    // Add account_type filter (brands match brands, individuals match individuals)
    if (props.superview?.normalized?.type) {
        query.account_type = [
            {
                id: props.superview.normalized.type,
            },
        ]
    }

    if (props.superview?.normalized?.country && lookalikeRetries.current <= 1) {
        query.country = [
            {
                id: props.superview.normalized.country,
            },
        ]
    }

    if (props.superview?.normalized?.language && lookalikeRetries.current < 3) {
        query.language = [
            {
                id: props.superview.normalized.language,
            },
        ]
    }

    // Add size-based filtering (avg_viewers for Twitch, avg_views for others)
    // Find creators within 50% to 200% of the current creator's size
    if (props.platformId === 'twitch') {
        const avgViewers = props.superview?.stream?.day_365?.aggregation?.viewers?.avg
        if (avgViewers) {

            let minAvgViewers = avgViewers * 0.25
            switch(lookalikeRetries.current) {
                case 1, 2 :
                    minAvgViewers = avgViewers * 0.15
                    break
                case 3, 4:
                    minAvgViewers = avgViewers * 0.10
                    break
            }

            query.avg_viewers = {
                min: Math.floor(minAvgViewers),
                max: Math.ceil(avgViewers * 4),
            }

        }
    } else {
        // For YouTube, TikTok, Instagram, X
        const contentTypeKey = props.contentType
        const avgViews = props.superview?.[contentTypeKey]?.day_365?.aggregation?.views?.avg

        let minAvgViews = avgViews * 0.5
        switch(lookalikeRetries.current) {
            case 1, 2 :
                minAvgViews = avgViews * 0.25
                break
            case 3, 4:
                minAvgViews = avgViews * 0.15
                break
        }

        if (avgViews) {
            query.avg_views = {
                min: Math.floor(minAvgViews),
                max: Math.ceil(avgViews * 4),
            }
        }
    }

    // Add interests filter with OR conjunction (match any of the creator's interests)
    // Only include interests with strength_rating >= 80
    if (props.analysis?.interests && props.analysis.interests.length > 0 && lookalikeRetries.current < 2) {
        const highAffinityInterests = props.analysis.interests
            .filter((interest) => interest.strength_rating >= 80)
            .map((interest) => ({
                id: interest.interest,
            }))

        if (highAffinityInterests.length > 0) {
            query.interests = {
                include: {
                    values: highAffinityInterests,
                    conjunction: '$or',
                },
            }
        }
    }

    // Prepare discovery API request body
    const requestBody = {
        collection: {
            content_type: props.contentType,
            day_range: String(365), // Default to 365 days
        },
        query,
        pagination: {
            limit: paging.value.perPage,
            skip: Math.max((paging.value.currentPage - 1) * paging.value.perPage, 0),
            sort: paging.value.sort,
        },
    }

    axios
        .post(`/discovery/${props.platformId}`, requestBody)
        .then(({ data }) => {
            // Filter out the current creator from results
            // eslint-disable-next-line no-underscore-dangle
            const filteredCreators = data.payload.creators.filter((creator) => {
                // eslint-disable-next-line no-underscore-dangle
                return creator?.superview?._id !== props.superview?._id
            })

            creators.value = filteredCreators

            paging.value.totalPages = Math.ceil(data.payload.count / paging.value.perPage)
            paging.value.totalRecords = data.payload.count

            if (filteredCreators.length === 0 && lookalikeRetries.current < lookalikeRetries.max && paging.value.currentPage === 1) {
                lookalikeRetries.current +=1
                fetchLookalikes()
                return
            }

            isLoading.value = false
        })
        .catch((error) => {
            console.error('Error fetching related creators', error)
            isLoading.value = false
        })
}

const onPagingChange = (updatedPaging) => {
    Object.assign(paging.value, updatedPaging)
    fetchLookalikes()
}

const onFilterChange = (updatedPaging) => {
    Object.assign(paging.value, updatedPaging)
    fetchLookalikes()
}

const onExportCreators = () => {
    store.commit('modal:setData', {
        modal_id: 'export_table',
        data: {
            is_open: true,
            file_name: 'Lookalikes',
            columns: {
                all: lookalikeColumns.value.filter(col =>
                    col.is_exportable
                ),
                enabled: lookalikeColumns.value.filter(col =>
                    enabledLookalikeColumns.value.includes(col.id) && col.is_exportable
                ),
            },
            rowCount: paging.value.totalRecords,

            providerFn: async () => {
                return creators.value
            },
        },
    })
}

// Watch for changes in platformUserId to refetch data
watch(
    () => ({
        platformUserId: props.platformUserId,
        platformId: props.platformId,
        contentType: props.contentType,
    }),
    (currentValue, previousValue) => {
        paging.value.currentPage = 1

        if (
            !previousValue
            || currentValue.platformId !== previousValue.platformId
            || currentValue.contentType !== previousValue.contentType
        ) {
            resetSorting()
        }

        creators.value = []
        fetchLookalikes()
    },
    { immediate: true },
)
</script>
